// MARK://dhk8/admin-shell/pages/modules/PurchaseOrderDetailPage
// Detalle de una orden de compra → dhk8-erp-purchase-orders `purchaseOrder(id)`.
// Muestra las líneas, el estado y las acciones de la máquina de estados:
//   DRAFT     → Enviar | Cancelar
//   SUBMITTED → Aprobar | Cancelar         (Aprobar también lo hace el proveedor
//                                           autorizando desde su bandeja)
//   APPROVED  → Recibir | Cancelar
//   PARTIAL   → Recibir | Cancelar
//   RECEIVED  → (terminal)
//   CANCELLED → (terminal)
//
// DS: estado como StatusPill; resumen en SummaryStrip; líneas en DataTable;
// recepción en SectionedSheet; cancelar con useConfirm; feedback con useToast.
//
// LÍMITES DEL CONTRATO (sin fabricar mutations):
//  · RECEPCIÓN: el grafo solo expone `receivePurchaseOrder(id, receivedCents, notes)`
//    — es POR MONTO, no por línea ni con almacén destino. Mantenemos recepción por
//    monto y enriquecemos el modal con el contexto por línea (esperado vs recibido).
//    TODO(grafo): receivePurchaseOrderLines([{lineId, qty, warehouseId}]) para
//    cambiar a recepción por línea + almacén.
//  · EDITAR DRAFT: el grafo ya expone `updatePurchaseOrder(id, ErpPurchaseOrderUpdateInput)`.
//    Editar solo se ofrece en estado DRAFT (botón oculto en cualquier otro estado);
//    el form (SectionedSheet) permite cambiar proveedor + notas + LÍNEAS (reemplazo
//    total del set). Tras éxito: toast + reload del detalle.
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Send, CheckCircle2, PackageCheck, XCircle, Pencil, Plus, Trash2 } from 'lucide-react'
import * as po from '../../lib/clients/purchase-orders.js'
import * as sup from '../../lib/clients/suppliers.js'
import * as cat from '../../lib/clients/catalog.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { mxn } from '../../lib/money.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill from '../../components/StatusPill.js'
import DataTable, { type DataTableColumn } from '../../components/DataTable.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import MoneyInput from '../../components/MoneyInput.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

// Línea editable del borrador (mismo shape que NewPurchaseOrderPage). Captura el
// costo en pesos; se convierte a centavos al guardar.
interface EditLine {
  key: string
  product: po.PurchasableProduct | null
  qty: number
  uom: string
  unitPesos: number
}
const editLineKey = () => `l-${Math.random().toString(36).slice(2, 8)}`

const PO_STATUS_TONE: Record<po.POStatus, 'ok' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  DRAFT: 'info',
  SUBMITTED: 'warn',
  APPROVED: 'ok',
  PARTIAL: 'warn',
  RECEIVED: 'ok',
  CANCELLED: 'danger',
}

export default function PurchaseOrderDetailPage() {
  const { id = '' } = useParams()
  const confirm = useConfirm()
  const { toast } = useToast()
  const order = useAsync((s) => po.getPurchaseOrder(id, s), [id])
  const suppliers = useAsync((s) => sup.listSuppliers(s), [id])
  // PO lines reference both PRODUCT and INGREDIENT (raw materials are the usual
  // case). Load all so we never render a raw UUID as the line's name.
  const products = useAsync((s) => cat.listAllProducts(s))
  // Catálogo comprable (PRODUCT + INGREDIENT) para el selector de líneas al editar.
  const purchasable = useAsync((s) => po.listPurchasableProducts(s))
  const session = useAuthStore((s) => s.session)

  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [receiving, setReceiving] = useState(false)
  const [receivePesos, setReceivePesos] = useState(0)

  // Editar borrador (solo DRAFT): proveedor + notas + líneas (reemplazo total).
  const [editing, setEditing] = useState(false)
  const [editSupplier, setEditSupplier] = useState<sup.Supplier | null>(null)
  const [editNotes, setEditNotes] = useState('')
  const [editLines, setEditLines] = useState<EditLine[]>([])
  const [editError, setEditError] = useState<string | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)

  const data = order.data
  const supplierName = data
    ? suppliers.data?.find((x) => x.id === data.supplierId)?.legalName ?? '(proveedor no encontrado)'
    : ''
  const productName = (pid: string) =>
    products.data?.find((p) => p.id === pid)?.name ?? '(insumo no encontrado)'

  async function run(label: string, fn: () => Promise<void>) {
    setActionError(null); setBusy(true)
    try { await fn(); order.reload(); toast(`${label} ✓`, 'success') }
    catch (e) { setActionError(`${label}: ${e instanceof Error ? e.message : 'error'}`) }
    finally { setBusy(false) }
  }

  function doSubmit() { void run('Enviada', () => po.submitPurchaseOrder(id)) }
  function doApprove() { void run('Aprobada', () => po.approvePurchaseOrder(id, session?.fullName ?? 'admin')) }
  async function doCancel() {
    const ok = await confirm({
      title: '¿Cancelar orden de compra?',
      message: 'La orden pasará a estado CANCELADA. Esta acción no se puede deshacer.',
      tone: 'danger', confirmLabel: 'Cancelar orden', cancelLabel: 'Volver',
    })
    if (!ok) return
    void run('Cancelada', () => po.cancelPurchaseOrder(id, 'Cancelada desde el admin'))
  }
  function openReceive() {
    setReceivePesos(data ? data.totalCents / 100 : 0)
    setActionError(null); setReceiving(true)
  }
  async function doReceive() {
    const cents = Math.round(receivePesos * 100)
    if (cents <= 0) { setActionError('Recibir: el monto debe ser mayor a 0.'); return }
    await run('Recepción registrada', () => po.receivePurchaseOrder(id, cents, 'Recepción desde el admin'))
    setReceiving(false)
  }

  function openEdit() {
    if (!data) return
    setEditSupplier(suppliers.data?.find((s) => s.id === data.supplierId) ?? null)
    setEditNotes(data.notes ?? '')
    const all = purchasable.data ?? []
    setEditLines(data.lines.map((l) => ({
      key: editLineKey(),
      product: all.find((p) => p.id === l.productId) ?? { id: l.productId, name: l.description ?? l.productId, sku: null, type: 'PRODUCT', unit: l.uom },
      qty: po.lineQty(l),
      uom: l.uom,
      unitPesos: l.unitCents / 100,
    })))
    setEditError(null); setEditing(true)
  }
  const fetchSuppliers = async (q: string): Promise<sup.Supplier[]> => {
    const all = suppliers.data ?? []
    const s = q.trim().toLowerCase()
    return (s ? all.filter((x) => x.legalName.toLowerCase().includes(s) || (x.rfc ?? '').toLowerCase().includes(s)) : all).slice(0, 50)
  }
  const fetchProducts = async (q: string): Promise<po.PurchasableProduct[]> => {
    const all = purchasable.data ?? []
    const s = q.trim().toLowerCase()
    return (s ? all.filter((p) => p.name.toLowerCase().includes(s) || (p.sku ?? '').toLowerCase().includes(s)) : all).slice(0, 50)
  }
  const editLineTotalCents = (l: EditLine): number => Math.round(l.qty * Math.round(l.unitPesos * 100))
  const editTotalCents = editLines.reduce((s, l) => s + editLineTotalCents(l), 0)
  function patchEditLine(key: string, patch: Partial<EditLine>) {
    setEditLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }
  function removeEditLine(key: string) {
    setEditLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls))
  }
  async function saveEdit() {
    if (!data) return
    setEditError(null)
    if (!editSupplier) { setEditError('Selecciona un proveedor.'); return }
    if (editLines.length === 0) { setEditError('Agrega al menos una línea.'); return }
    for (const l of editLines) {
      if (!l.product) { setEditError('Cada línea necesita un producto.'); return }
      if (l.qty <= 0) { setEditError('La cantidad de cada línea debe ser mayor a 0.'); return }
      if (l.unitPesos < 0) { setEditError('El costo unitario no puede ser negativo.'); return }
    }
    setSavingEdit(true)
    try {
      await po.updatePurchaseOrder(data.id, {
        supplierId: editSupplier.id,
        notes: editNotes.trim() ? editNotes.trim() : null,
        lines: editLines.map((l) => ({ productId: l.product!.id, qty: l.qty, uom: l.uom, unitCents: Math.round(l.unitPesos * 100) })),
      })
      setEditing(false); order.reload(); toast('Borrador actualizado', 'success')
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'No se pudo actualizar el borrador.')
    } finally { setSavingEdit(false) }
  }

  const lineColumns: DataTableColumn<po.POLine>[] = [
    { key: 'producto', header: 'Producto', render: (l) => l.description ?? productName(l.productId) },
    { key: 'cantidad', header: 'Cantidad', align: 'right', render: (l) => <span className="adm-mono">{po.lineQty(l)} {unitShort(l.uom)}</span> },
    { key: 'recibido', header: 'Recibido', align: 'right', render: (l) => <span className="adm-mono">{po.lineReceived(l)} {unitShort(l.uom)}</span> },
    { key: 'unitario', header: 'Costo unit.', align: 'right', render: (l) => <span className="adm-mono">{mxn(l.unitCents)}</span> },
    { key: 'total', header: 'Total', align: 'right', render: (l) => <span className="adm-mono">{mxn(l.totalCents)}</span> },
  ]

  // Solo se puede editar el borrador (DRAFT); en cualquier otro estado el botón se oculta.
  const canEditDraft = data?.status === 'DRAFT'

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <Link to="/purchase-orders" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginBottom: 6 }}><ArrowLeft size={15} /> Órdenes de compra</Link>
          <h1 className="adm-page__title">OC <span className="adm-mono">{id.slice(0, 8).toUpperCase()}</span></h1>
          <p className="adm-page__sub">Detalle de la orden de compra</p>
        </div>
        {data && <div className="adm-page__actions">
          <StatusPill tone={PO_STATUS_TONE[data.status]} label={po.PO_STATUS_LABEL[data.status]} />
        </div>}
      </div>

      {asyncState(order.loading, order.error, order.reload)}
      {actionError && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 12 }}>{actionError}</div>}

      {data && (
        <>
          <div style={{ marginBottom: 'var(--pos-space-3, 12px)' }}>
            <SummaryStrip items={[
              { label: 'Proveedor', value: supplierName },
              { label: 'Total', value: mxn(data.totalCents) },
              { label: 'Líneas', value: data.lines.length },
              { label: 'Creada', value: new Date(data.createdAt).toLocaleDateString('es-MX') },
            ]} />
          </div>

          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title">Resumen</h2>
            <div className="adm-grid adm-grid--wide">
              <div><div className="adm-row__meta">Proveedor</div><div className="adm-row__title">{supplierName}</div></div>
              <div><div className="adm-row__meta">Estado</div><div className="adm-row__title"><StatusPill tone={PO_STATUS_TONE[data.status]} label={po.PO_STATUS_LABEL[data.status]} /></div></div>
              <div><div className="adm-row__meta">Total</div><div className="adm-row__title adm-mono">{mxn(data.totalCents)}</div></div>
              <div><div className="adm-row__meta">Creada</div><div className="adm-row__title">{new Date(data.createdAt).toLocaleString('es-MX')}</div></div>
              {data.networkId && <div><div className="adm-row__meta">Enlace de red</div><div className="adm-row__title">{data.networkId}</div></div>}
            </div>
            {data.notes && <p className="adm-row__meta" style={{ marginTop: 8 }}>Notas: {data.notes}</p>}
          </div>

          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title">Líneas</h2>
            <DataTable
              columns={lineColumns}
              rows={data.lines}
              getRowKey={(l) => l.id}
              zebra
              emptyText="Esta orden no tiene líneas."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, fontWeight: 700, fontSize: 'var(--pos-text-lg)' }}>
              Total: <span className="adm-mono" style={{ marginLeft: 6 }}>{mxn(data.totalCents)}</span>
            </div>
          </div>

          {data.receipts && data.receipts.length > 0 && (
            <div className="adm-card adm-card--pad">
              <h2 className="adm-card__title">Recepciones</h2>
              <div className="adm-list">
                {data.receipts.map((r) => (
                  <div key={r.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">Recepción <span className="adm-mono">{r.id.slice(0, 8).toUpperCase()}</span></div>
                      <div className="adm-row__meta">{new Date(r.receivedAt).toLocaleString('es-MX')}{r.notes ? ` · ${r.notes}` : ''}</div>
                    </div>
                    <div className="adm-row__tail"><span className="adm-row__title adm-mono">{mxn(r.receivedCents)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title">Acciones</h2>
            <div className="adm-page__actions">
              {data.status === 'DRAFT' && (
                <button className="adm-btn adm-btn--primary" onClick={doSubmit} disabled={busy}><Send size={16} /> Enviar</button>
              )}
              {canEditDraft && (
                <button className="adm-btn" onClick={openEdit} disabled={busy}><Pencil size={16} /> Editar borrador</button>
              )}
              {data.status === 'SUBMITTED' && (
                <button className="adm-btn adm-btn--primary" onClick={doApprove} disabled={busy}><CheckCircle2 size={16} /> Aprobar</button>
              )}
              {(data.status === 'APPROVED' || data.status === 'PARTIAL') && (
                <button className="adm-btn adm-btn--primary" onClick={openReceive} disabled={busy}><PackageCheck size={16} /> Recibir</button>
              )}
              {data.status !== 'RECEIVED' && data.status !== 'CANCELLED' && (
                <button className="adm-btn adm-btn--danger" onClick={doCancel} disabled={busy}><XCircle size={16} /> Cancelar</button>
              )}
              {(data.status === 'RECEIVED' || data.status === 'CANCELLED') && (
                <span className="adm-row__meta">Orden en estado terminal — sin acciones disponibles.</span>
              )}
            </div>
            {data.status === 'SUBMITTED' && (
              <p className="adm-row__meta" style={{ marginTop: 8 }}>
                Esta orden también puede aprobarse desde la <Link to="/purchase-orders/inbox">bandeja del proveedor</Link> (autorización cross-entity).
              </p>
            )}
          </div>

          <div className="adm-card adm-card--pad">
            <h2 className="adm-card__title">Historial</h2>
            <AuditTrail entityId={id} />
          </div>
        </>
      )}

      <SectionedSheet
        open={receiving}
        title="Recibir orden de compra"
        onClose={() => setReceiving(false)}
        footer={<>
          <button className="adm-btn" onClick={() => setReceiving(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={doReceive} disabled={busy}>{busy ? 'Procesando…' : 'Confirmar recepción'}</button>
        </>}
      >
        {/* Contexto por línea: el grafo recibe por MONTO (no por línea/almacén),
            pero mostramos lo esperado vs recibido para capturar mejor el monto. */}
        {data && data.lines.length > 0 && (
          <FormSection title="Líneas esperadas">
            <div className="adm-list">
              {data.lines.map((l) => (
                <div key={l.id} className="adm-row">
                  <div className="adm-row__main">
                    <div className="adm-row__title">{l.description ?? productName(l.productId)}</div>
                    <div className="adm-row__meta">
                      esperado <span className="adm-mono">{po.lineQty(l)} {unitShort(l.uom)}</span>
                      {po.lineReceived(l) > 0 ? <> · recibido <span className="adm-mono">{po.lineReceived(l)} {unitShort(l.uom)}</span></> : null}
                    </div>
                  </div>
                  <div className="adm-row__tail"><span className="adm-row__title adm-mono">{mxn(l.totalCents)}</span></div>
                </div>
              ))}
            </div>
          </FormSection>
        )}
        <FormSection title="Monto recibido">
          <p className="adm-row__meta">Confirma el monto recibido. Si es menor al total, la orden queda PARCIAL; si lo cubre, RECIBIDA y el inventario sube.</p>
          <label className="adm-field">
            <span className="adm-field__label">Monto recibido (MXN)</span>
            <input className="adm-input" type="number" min={0} step="0.01" value={receivePesos} onChange={(e) => setReceivePesos(Number(e.target.value))} style={{ fontFamily: 'var(--font-mono)' }} />
          </label>
          <p className="adm-row__meta">
            Recepción por línea + almacén destino: pendiente en el grafo (<code>receivePurchaseOrderLines</code> aún no existe).
          </p>
        </FormSection>
      </SectionedSheet>

      {/* Editar borrador — proveedor + notas + líneas (reemplazo total) */}
      <SectionedSheet
        open={editing}
        title="Editar borrador"
        onClose={() => { setEditing(false); setEditError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={savingEdit || !editSupplier || editLines.length === 0}>{savingEdit ? 'Guardando…' : 'Guardar cambios'}</button>
        </>}
      >
        {editError && <div className="adm-banner adm-banner--danger" role="alert">{editError}</div>}
        <FormSection title="Cabecera">
          <label className="adm-field">
            <span className="adm-field__label">Proveedor</span>
            <EntityCombobox
              value={editSupplier}
              onChange={setEditSupplier}
              fetchOptions={fetchSuppliers}
              renderOption={(s) => <span>{s.legalName}{s.rfc ? <span className="adm-muted"> · {s.rfc}</span> : null}</span>}
              getOptionValue={(s) => s.id}
              getOptionLabel={(s) => s.legalName}
              placeholder="Buscar proveedor…"
              allowClear
            />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Notas (opcional)</span>
            <textarea className="adm-textarea" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Reorden de café en grano — stock bajo" rows={2} />
          </label>
        </FormSection>
        <FormSection title="Líneas de producto">
          <div className="adm-list">
            {editLines.map((l) => (
              <div key={l.key} className="adm-row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <div className="adm-row__main" style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                  <label className="adm-field" style={{ margin: 0 }}>
                    <span className="adm-field__label">Producto</span>
                    <EntityCombobox
                      value={l.product}
                      onChange={(p) => patchEditLine(l.key, { product: p })}
                      fetchOptions={fetchProducts}
                      renderOption={(p) => <span>{p.name}{p.sku ? <span className="adm-muted"> · {p.sku}</span> : null}</span>}
                      getOptionValue={(p) => p.id}
                      getOptionLabel={(p) => p.name}
                      placeholder="Buscar producto…"
                      allowClear
                    />
                  </label>
                  <label className="adm-field" style={{ margin: 0 }}>
                    <span className="adm-field__label">Cantidad</span>
                    <input className="adm-input" type="number" min={0} step="0.01" value={l.qty} onChange={(e) => patchEditLine(l.key, { qty: Number(e.target.value) })} />
                  </label>
                  <div style={{ margin: 0 }}>
                    <UnitPicker label="UOM" value={l.uom} onChange={(uom) => patchEditLine(l.key, { uom })} />
                  </div>
                  <label className="adm-field" style={{ margin: 0 }}>
                    <span className="adm-field__label">Costo unitario</span>
                    <MoneyInput valueCents={Math.round(l.unitPesos * 100)} onChangeCents={(c) => patchEditLine(l.key, { unitPesos: c / 100 })} />
                  </label>
                  <label className="adm-field" style={{ margin: 0 }}>
                    <span className="adm-field__label">Total línea</span>
                    <input className="adm-input" value={mxn(editLineTotalCents(l))} disabled aria-readonly="true" style={{ fontFamily: 'var(--font-mono)' }} />
                  </label>
                  <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeEditLine(l.key)} disabled={editLines.length <= 1} aria-label="Quitar línea"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button className="adm-btn adm-btn--sm" onClick={() => setEditLines((ls) => [...ls, { key: editLineKey(), product: null, qty: 1, uom: 'KG', unitPesos: 0 }])}><Plus size={15} /> Agregar línea</button>
            <div style={{ fontSize: 'var(--pos-text-lg)', fontWeight: 700 }}>Total: {mxn(editTotalCents)}</div>
          </div>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
